import React from "react";
import SeoLandingLayout from "./SeoLandingLayout";
import { CheckCircle, Sparkles, Map as MapIcon, Users, Shield, Feather } from "lucide-react";
import { NARRATOR_LIST } from "@/data/models";

const FEATURES = [
  { icon: Sparkles, title: "Memory Codex", body: "Named NPCs, threads, milestones and inventory changes are tracked and fed back to the narrator, which improves continuity across long sagas." },
  { icon: MapIcon, title: "Illustrated scenes", body: "Automatic scene art on every major beat — you always have a picture of where you are and who's in the room." },
  { icon: Users, title: "Switchable AI narrators", body: `${NARRATOR_LIST}. Swap mid-saga while the Codex carries story details forward.` },
  { icon: Shield, title: "Light RPG elements, when you want them", body: "Occasional dice, skills, equipment and inventory are opt-in — silent for pure novel players, on for players who want a bit more structure." },
  { icon: Feather, title: "Literary prose", body: "The narrator writes in a novelist's voice, with sensory detail and character-specific dialogue. Sagas read like a book you'd want to keep." },
  { icon: CheckCircle, title: "Free daily turns", body: "10 free turns per day on the Wanderer tier — no credit card. Try before you commit to a plan." },
];

const FAQ = [
  {
    q: "Why might I look for an AI Dungeon alternative?",
    a: "Players often compare tools based on memory controls, scene visuals, pricing structure, and narrator options. TalesTalesTales focuses on a Memory Codex, automatic scene illustrations, and multiple switchable narrators. Both products are actively developed; the better fit depends on your workflow and content preferences."
  },
  {
    q: "How does memory work here?",
    a: "TalesTalesTales runs a side-channel Memory Codex that records named NPCs, threads, milestones, and inventory changes on each turn, separate from the narrator's context window, and feeds that record back to the narrator to improve continuity across model swaps and longer sagas. It does not guarantee perfect recall over very long sagas."
  },
  {
    q: "Can I import my old sagas?",
    a: "Not automatically — different platforms use different save formats. You can, however, describe your setting and hero in plain English on the campaign-creation page and the AI will rebuild the world for you."
  },
  {
    q: "Is it really free?",
    a: "Yes — 10 turns per day on the free Wanderer tier, forever, no card. Paid tiers add more turns and premium narrators."
  },
  {
    q: "Is there a mature mode?",
    a: "Yes, opt-in and 18+ only, gated behind age verification and a paid subscription. Standard sagas remain safe by default."
  },
];

export default function AiDungeonAlternative() {
  return (
    <SeoLandingLayout
      testId="seo-ai-dungeon-alternative-page"
      path="/ai-dungeon-alternative"
      title="AI Dungeon Alternative | TalesTalesTales"
      description="Looking for an AI Dungeon alternative? TalesTalesTales is an interactive AI Novel with a Memory Codex, illustrated scenes, switchable AI narrators, and free daily turns."
      eyebrow="AI Dungeon Alternative"
      h1="AI Dungeon Alternative"
      h1Accent="for players who want persistent continuity"
      lede="If you're looking for an AI Dungeon alternative, this page explains what TalesTalesTales offers today in plain terms: a persistent Memory Codex, illustrated scenes, switchable narrators, and optional mechanics."
      body={(
        <>
          <p>
            <strong>Comparison methodology:</strong> this page compares product behaviors that are visible in TalesTalesTales today and publicly documented features on competitor websites at the time of writing. Third-party plans and features can change; verify current details on the vendor&rsquo;s official pricing and help pages before deciding.
          </p>
          <p>
            A common frustration with early AI storytelling tools is context drift — the AI
            forgetting NPCs, factions, or a promise you made three chapters ago. TalesTalesTales
            works against that with a <strong>Memory Codex</strong> that lives outside the
            narrator&rsquo;s context window. Named NPCs, threads, inventory changes and
            milestones are written to the codex on each turn and fed back to the narrator, which
            improves continuity over long sagas without guaranteeing perfect recall.
          </p>
          <p>
            The second thing is <strong>illustrated scenes</strong>. Every major beat gets its own
            auto-generated illustration tuned for the setting — you always have a picture of where you
            are. It makes the story feel like an actual book, not a chat window.
          </p>
          <p>
            The third is <strong>choice of narrator</strong>. You aren&rsquo;t locked to one model.
            Claude Sonnet 4.5 is the default; Claude Haiku 4.5, GPT-5 and Gemini 2.5 Pro are one click
            away. Each family has a different tonal register &mdash; try a few, keep the one that fits your
            saga. For adult roleplay on the Mature Realms add-on, uncensored OpenRouter models are
            available. You can swap narrators mid-saga and the Memory Codex carries story details
            across the swap.
          </p>
          <p>
            Light RPG elements (occasional dice, HP, party companions) are opt-in flavour, not a full
            tabletop simulation. You can play it as a pure novel, or turn them on for a bit more
            structure in your text-based adventure.
          </p>
          <p>
            We don&rsquo;t speculate about other tools&rsquo; roadmaps. What we can confirm is what
            TalesTalesTales ships today: a Memory Codex, illustrated scenes, four narrators, light
            RPG elements when you want them, silent when you don&rsquo;t, and 10 free turns every day on a
            no-card account.
          </p>
          <p>
            <strong>Non-affiliation:</strong> TalesTalesTales is independent and is not affiliated with AI Dungeon or its parent company.
          </p>
        </>
      )}
      features={FEATURES}
      faq={FAQ}
      comparison={{
        competitor: "AI Dungeon",
        rows: [
          { feature: "Persistent memory model", us: "Memory Codex tracks NPCs, threads, milestones, inventory", them: "Vendor-defined memory tools; check official docs for current behavior" },
          { feature: "Scene illustrations", us: "Automatic in-line art on major beats", them: "Availability and workflow vary by plan and feature set" },
          { feature: "Narrator options", us: NARRATOR_LIST, them: "Vendor-defined model options and tiers" },
          { feature: "Rules layer", us: "Light RPG elements: dice, HP, equipment, inventory", them: "Narrative-first flow; mechanics style depends on product mode" },
          { feature: "Entry pricing", us: "10 turns/day free, no card required", them: "See official pricing page for current free and paid terms" },
          { feature: "Mature content controls", us: "Opt-in Mature Realms, age-verified, paid tier", them: "See official policy and plan documentation" },
          { feature: "World publishing", us: "Community library", them: "User-created content features vary by current product settings" },
        ],
      }}
    />
  );
}